/**
 * Admin API Service
 *
 * Maintenance (nur ADMIN):
 * POST   /admin/update_all_mangas       - Mangas global updaten
 * POST   /admin/download_missing_covers - Fehlende Covers laden
 * POST   /admin/reindex_band_counts     - Band-Counts neu berechnen
 * POST   /admin/rescan_all_isbn         - Alle ISBNs neu scannen
 * POST   /admin/fix_manga_covers        - Cover-Media-Keys migrieren
 * POST   /admin/sync_bands              - Fehlende Bände anlegen
 * DELETE /admin/bands/{band_id}         - Band global löschen
 * POST   /admin/audit_legacy_users      - Bestandsuser prüfen/bereinigen
 *
 * Monitoring (ADMIN + MODERATOR):
 * GET    /admin/logs                    - System-Logs abrufen
 * GET    /admin/stats                   - Admin-System-Statistiken
 * GET    /admin/job-runs                - Log der Scheduled Jobs
 *
 * User Management:
 * GET    /admin/users                   - User-Liste
 * PATCH  /admin/users/{id}/role         - Rolle eines Users ändern (nur ADMIN)
 * DELETE /admin/users/{id}              - User löschen (nur ADMIN)
 * PATCH  /admin/users/{id}/password     - Passwort eines Users zurücksetzen (nur ADMIN)
 *
 * Shop-/Affiliate-Verwaltung (nur ADMIN):
 * GET    /admin/shop-providers
 * POST   /admin/shop-providers
 * PUT    /admin/shop-providers/{id}
 * DELETE /admin/shop-providers/{id}
 *
 * Audit-Log (ADMIN + MODERATOR, §59):
 * GET    /admin/audit-log
 *
 * Katalogverwaltung (nur ADMIN, §36):
 * GET/POST/PUT/DELETE /admin/genres|authors|publishers, POST .../merge
 *
 * API-Nutzung (ADMIN + MODERATOR):
 * GET    /admin/api-usage
 *
 * Pro-User Rate-Limits (nur ADMIN):
 * GET/PUT/DELETE /admin/users/{id}/rate-limits/**
 *
 * Manga-/Band-Verwaltung (nur ADMIN, §48/§49):
 * POST/PUT /admin/mangas, PATCH .../active
 * POST /admin/mangas/{manga_id}/bands, PUT /admin/bands/{id}, PATCH .../active
 */

import { get, post, put, patch, del } from '../client';

// ============================================================================
// BACKEND ACTIONS
// ============================================================================

export const updateAllMangas = async () => post('/admin/update_all_mangas');
export const downloadMissingCovers = async () => post('/admin/download_missing_covers');
export const reindexBandCounts = async () => post('/admin/reindex_band_counts');
export const rescanAllIsbn = async () => post('/admin/rescan_all_isbn');
export const deleteBand = async (bandId) => del(`/admin/bands/${bandId}`);

/**
 * Fehlende Bände für alle Mangas anlegen (läuft auch täglich automatisch um 03:00 Uhr)
 * @returns {Promise<{success: boolean, data?: {created: number}, error?: object}>}
 */
export const syncBands = async () => post('/admin/sync_bands');

/**
 * Einmalige Prüfung/Bereinigung der Bestandsuser (Case-Kollisionen, invalide
 * E-Mail-Domains, zu kurze Usernames, Passwort-Hash-Format).
 * @param {boolean} dryRun - Default true; false löscht ungültige User mit invalider Domain
 * @returns {Promise<{success: boolean, data?: object, error?: object}>}
 */
export const auditLegacyUsers = async (dryRun = true) =>
  post(`/admin/audit_legacy_users?dryRun=${dryRun}`);

// ============================================================================
// LOGS
// ============================================================================

/**
 * System-Logs laden
 * @param {{ level?: string, from?: string, to?: string, pageSize?: number, cursor?: number }} filters
 * @returns {Promise<{success: boolean, data?: {items: object[], next_cursor: number|null}, error?: object}>}
 */
export const getLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.level)    params.set('level',    filters.level);
  if (filters.from)     params.set('from',     filters.from);
  if (filters.to)       params.set('to',       filters.to);
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.cursor)   params.set('cursor',   String(filters.cursor));
  const query = params.toString() ? `?${params}` : '';
  return get(`/admin/logs${query}`);
};

// ============================================================================
// ADMIN STATS
// ============================================================================

/**
 * Admin-Systemstatistiken (User-Anzahl, DB-Größe, ISBN-Lookup-Stats, etc.)
 * @returns {Promise<{success: boolean, data?: object, error?: object}>}
 */
export const getAdminStats = async () => get('/admin/stats');

/**
 * Log der Scheduled Jobs (Release-Update, Band-Sync)
 * @param {{ job_name?: string, limit?: number }} options
 * @returns {Promise<{success: boolean, data?: object[], error?: object}>}
 */
export const getJobRuns = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.job_name) params.set('job_name', options.job_name);
  params.set('limit', String(options.limit ?? 50));
  return get(`/admin/job-runs?${params}`);
};

// ============================================================================
// USER MANAGEMENT
// ============================================================================

/**
 * User-Liste abrufen
 * @returns {Promise<{success: boolean, data?: {items: object[], next_cursor: number|null}, error?: object}>}
 */
export const getUsers = async (query = '', options = {}) => {
  const params = new URLSearchParams();
  if (query)          params.set('q',      query);
  if (options.cursor) params.set('cursor', options.cursor);
  params.set('limit', String(options.limit ?? 50));
  return get(`/admin/users?${params}`);
};

/**
 * Rolle eines Users ändern (nur ADMIN)
 * Req: { role: 0|1|2|3|4|5 }
 */
export const updateUserRole = async (userId, role) =>
  patch(`/admin/users/${userId}/role`, { role });

/**
 * User löschen (inkl. aller Daten, nur ADMIN)
 */
export const deleteUser = async (userId) => del(`/admin/users/${userId}`);

/**
 * Passwort eines Users zurücksetzen (nur ADMIN, kein aktuelles PW nötig)
 * Req: { new_password: string }
 */
export const resetUserPassword = async (userId, newPassword) =>
  patch(`/admin/users/${userId}/password`, { new_password: newPassword });

// ============================================================================
// SHOP-/AFFILIATE-VERWALTUNG (nur ADMIN)
// ============================================================================

/**
 * Alle Shop-Provider laden
 * @returns {Promise<{success: boolean, data?: ShopProvider[], error?: object}>}
 */
export const getShopProviders = async () => get('/admin/shop-providers');

/**
 * Neuen Shop-Provider anlegen
 * @param {Partial<ShopProvider>} provider - { name, url_template, affiliate_param_key?, affiliate_param_value?, logo_media_key?, active?, sort_order? }
 */
export const createShopProvider = async (provider) => post('/admin/shop-providers', provider);

/**
 * Shop-Provider aktualisieren (vollständig)
 * @param {number} id
 * @param {Partial<ShopProvider>} provider
 */
export const updateShopProvider = async (id, provider) => put(`/admin/shop-providers/${id}`, provider);

/**
 * Shop-Provider löschen
 * @param {number} id
 */
export const deleteShopProvider = async (id) => del(`/admin/shop-providers/${id}`);

// ============================================================================
// AUDIT LOG (ADMIN + MODERATOR, §59)
// ============================================================================

/**
 * Audit-Log laden (sicherheitsrelevante Admin-Aktionen)
 * @param {{ cursor?: number, limit?: number }} options
 * @returns {Promise<{success: boolean, data?: {items: object[], next_cursor: number|null}, error?: object}>}
 */
export const getAuditLog = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.cursor) params.set('cursor', String(options.cursor));
  params.set('limit', String(options.limit ?? 100));
  return get(`/admin/audit-log?${params}`);
};

// ============================================================================
// KATALOGVERWALTUNG: GENRES / AUTOREN / VERLAGE (nur ADMIN, §36)
// ============================================================================
// resource: 'genres' | 'authors' | 'publishers'

/** Liste eines Katalogs laden */
export const getCatalog = async (resource) => get(`/admin/${resource}`);

/** Neuen Eintrag anlegen */
export const createCatalogEntry = async (resource, name) => post(`/admin/${resource}`, { name });

/** Eintrag umbenennen - propagiert auf alle Mangas mit dem alten Namen */
export const renameCatalogEntry = async (resource, id, name) => put(`/admin/${resource}/${id}`, { name });

/** Eintrag {id} in target_id zusammenführen, {id} wird danach gelöscht */
export const mergeCatalogEntry = async (resource, id, targetId) =>
  post(`/admin/${resource}/${id}/merge`, { target_id: targetId });

/** Eintrag löschen */
export const deleteCatalogEntry = async (resource, id) => del(`/admin/${resource}/${id}`);

// ============================================================================
// API-NUTZUNGSSTATISTIK (ADMIN + MODERATOR)
// ============================================================================

/**
 * Zeigt, wie oft welcher eingeloggte User welchen Endpoint aufruft - Grundlage
 * um Session-Missbrauch (Skript statt Frontend) zu erkennen.
 * @param {{ user_id?: number, limit?: number }} options
 * @returns {Promise<{success: boolean, data?: object[], error?: object}>}
 */
export const getApiUsage = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.user_id) params.set('user_id', String(options.user_id));
  params.set('limit', String(options.limit ?? 100));
  return get(`/admin/api-usage?${params}`);
};

// ============================================================================
// PRO-USER RATE-LIMIT-OVERRIDES (nur ADMIN)
// ============================================================================

/** Aktuelle Rate-Limit-Overrides eines Users laden */
export const getUserRateLimits = async (userId) => get(`/admin/users/${userId}/rate-limits`);

/**
 * Override für einen Limit-Key setzen (z.B. "isbn-lookup")
 * @param {number} userId
 * @param {string} limitKey
 * @param {{ max_attempts: number, window_seconds: number }} data
 */
export const setUserRateLimit = async (userId, limitKey, data) =>
  put(`/admin/users/${userId}/rate-limits/${limitKey}`, data);

/** Override entfernen - fällt zurück auf den Code-Default */
export const deleteUserRateLimit = async (userId, limitKey) =>
  del(`/admin/users/${userId}/rate-limits/${limitKey}`);

// ============================================================================
// MANGA-/BAND-VERWALTUNG (nur ADMIN, §48/§49)
// ============================================================================

/**
 * Neue Manga-Serie anlegen (Admin-Katalogpflege, unabhängig von ISBN-Import)
 * @param {{ title: string, description?: string, status?: string, publisher?: string, genres?: string[], cover_media_key?: string }} data
 */
export const createManga = async (data) => post('/admin/mangas', data);

/** Manga-Metadaten aktualisieren (gleiche Felder wie createManga) */
export const updateManga = async (id, data) => put(`/admin/mangas/${id}`, data);

/** Manga aktivieren/deaktivieren (Soft-Delete statt Hard-Delete, §57) */
export const setMangaActive = async (id, active) => patch(`/admin/mangas/${id}/active`, { active });

/**
 * Neuen Band zu einer Serie hinzufügen (Admin-Katalogpflege)
 * @param {number} mangaId
 * @param {{ band_number: number, band_title?: string, author?: string, publisher?: string, published_date?: string, language?: string, cover_media_key?: string }} data
 */
export const createBand = async (mangaId, data) => post(`/admin/mangas/${mangaId}/bands`, data);

/** Band-Metadaten korrigieren (Bandnummer/Datum/Cover, §49) */
export const updateBand = async (id, data) => put(`/admin/bands/${id}`, data);

/** Band aktivieren/deaktivieren (Soft-Delete) */
export const setBandActive = async (id, active) => patch(`/admin/bands/${id}/active`, { active });

// ============================================================================
// TYPES (JSDoc)
// ============================================================================

/**
 * @typedef {object} ShopProvider
 * @property {number} id
 * @property {string} name
 * @property {string} url_template
 * @property {string|null} affiliate_param_key
 * @property {string|null} affiliate_param_value
 * @property {string|null} logo_media_key
 * @property {boolean} active
 * @property {number} sort_order
 */

// ============================================================================
// ROLE HELPERS
// ============================================================================

export const isAdmin     = (role) => role === 0;
export const isModerator = (role) => role <= 1;

export const ROLE_LABELS = {
  0: 'Admin',
  1: 'Moderator',
  2: 'Supporter',
  3: 'Premium',
  4: 'User',
  5: 'Guest',
};

